import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_BorderImages = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🖼️ CSS Border Images</h1>

      <p className="tutorial-intro">
        The <code>border-image</code> property lets you replace regular borders
        with <b>images</b> or <b>gradients</b>. This creates stylish frames,
        buttons, and layouts without extra graphics.
      </p>

      {/* ✅ Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🔑 Key Points</h2>
        <ul
          style={{
            lineHeight: "1.8",
            fontSize: "1.05rem",
            paddingLeft: "25px",
          }}
        >
          <li>
            <code>border-image-source</code> → The image or gradient used.
          </li>
          <li>
            <code>border-image-slice</code> → Defines how the image is split
            into corners and sides.
          </li>
          <li>
            <code>border-image-repeat</code> → Can be <b>stretch</b>,{" "}
            <b>repeat</b>, or <b>round</b>.
          </li>
          <li>
            Always set <code>border-width</code>, or the image won’t show.
          </li>
          <li>Works with both images and CSS gradients.</li>
        </ul>
      </div>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 1: Pattern Border</h2>
        <CodeCopyBlock
          code={`.pattern-border {
  border: 20px solid transparent;
  border-image: url("https://i.imgur.com/8w0pU8h.png") 30 round;
}`}
          language="css"
        />

        <div
          style={{
            border: "20px solid transparent",
            borderImage: 'url("https://i.imgur.com/8w0pU8h.png") 30 round',
            padding: "20px",
            textAlign: "center",
            marginTop: "1rem",
          }}
        >
          Pattern Border Image
        </div>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 2: Gradient Border</h2>
        <CodeCopyBlock
          code={`.gradient-border {
  border: 12px solid transparent;
  border-image: linear-gradient(45deg, #3b82f6, #9333ea) 1;
}`}
          language="css"
        />

        <div
          style={{
            border: "12px solid transparent",
            borderImage: "linear-gradient(45deg, #3b82f6, #9333ea) 1",
            padding: "20px",
            textAlign: "center",
            borderRadius: "8px",
            marginTop: "1rem",
          }}
        >
          Gradient Border
        </div>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 3: Decorative Frame</h2>
        <CodeCopyBlock
          code={`.frame-border {
  border: 25px solid transparent;
  border-image: url("https://i.imgur.com/1mR2Y5J.png") 40 round;
  border-radius: 12px;
}`}
          language="css"
        />

        <div
          style={{
            border: "25px solid transparent",
            borderImage: 'url("https://i.imgur.com/1mR2Y5J.png") 40 round',
            borderRadius: "12px",
            padding: "20px",
            marginTop: "1rem",
            background: "#fff",
            maxWidth: "300px",
          }}
        >
          <h3>Decorative Frame</h3>
          <p>This card looks like a styled picture frame.</p>
        </div>
      </div>

      {/* Example 4 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 4: Double Effect Border</h2>
        <CodeCopyBlock
          code={`.double-border {
  border: 20px double transparent;
  border-image: url("https://i.imgur.com/Xh0x7wL.png") 30 stretch;
}`}
          language="css"
        />

        <div
          style={{
            border: "20px double transparent",
            borderImage: 'url("https://i.imgur.com/Xh0x7wL.png") 30 stretch',
            padding: "20px",
            marginTop: "1rem",
            textAlign: "center",
          }}
        >
          Double Effect Border
        </div>
      </div>

      {/* Example 5 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 5: Fancy Button</h2>
        <CodeCopyBlock
          code={`.fancy-btn {
  padding: 12px 24px;
  font-size: 16px;
  color: #fff;
  background: #111;
  border: 8px solid transparent;
  border-image: linear-gradient(90deg, #ef4444, #f59e0b, #10b981, #3b82f6) 1;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.fancy-btn:hover {
  background: #222;
  transform: scale(1.05);
}`}
          language="css"
        />

        <button
          style={{
            padding: "12px 24px",
            fontSize: "16px",
            color: "#fff",
            background: "#111",
            border: "8px solid transparent",
            borderImage:
              "linear-gradient(90deg, #ef4444, #f59e0b, #10b981, #3b82f6) 1",
            borderRadius: "8px",
            cursor: "pointer",
            transition: "all 0.3s ease",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.transform = "scale(1.05)")
          }
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        >
          Fancy Button
        </button>
      </div>

      {/* 🚀 Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Try mixing <b>SVGs</b> and <b>gradients</b> as border images.
          Experiment with <code>round</code>, <code>stretch</code>, and{" "}
          <code>repeat</code> to see how images scale. Combine with{" "}
          <code>border-radius</code> for advanced styles.
        </p>
      </div>
    </div>
  );
};

export default CSS_BorderImages;
